import { useEffect } from "react";
import "./RazorLeafAnimation.css";

/**
 * PROFESSIONAL RAZOR LEAF ANIMATION
 * A grass-type move that sends sharp leaves to slice the opponent
 * 
 * Key Features:
 * - Multiple spinning razor-sharp leaves
 * - Curved trajectory paths
 * - Slicing impact effects
 * - Professional game-quality visual effects
 */
export default function RazorLeafAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest(".battle-bg") || playerEl.closest(".battle-container");
    const pRect = playerEl.getBoundingClientRect();
    const oRect = opponentEl.getBoundingClientRect();
    const cRect = container.getBoundingClientRect();

    const containerX = cRect.left;
    const containerY = cRect.top;

    // Player center position
    const startX = pRect.left - containerX + pRect.width / 2;
    const startY = pRect.top - containerY + pRect.height / 2;
    
    // Opponent center position
    const endX = oRect.left - containerX + oRect.width / 2;
    const endY = oRect.top - containerY + oRect.height / 2;

    const flightDuration = 600;
    const impactDuration = 300;
    const delayStep = 80;
    const leafCount = 5;

    const leaves = [];
    const cleanupElements = [];

    // Player preparation effect
    playerEl.classList.add("razor-leaf-prepare");
    setTimeout(() => playerEl.classList.remove("razor-leaf-prepare"), 400);

    // Create leaves with different paths
    for (let i = 0; i < leafCount; i++) {
      setTimeout(() => {
        const leaf = document.createElement("div");
        leaf.className = "razor-leaf";
        leaf.style.left = `${startX}px`;
        leaf.style.top = `${startY}px`;
        
        // Calculate a curved path
        const pathType = i % 3; // 0: direct, 1: arc up, 2: arc down
        const controlPointX = startX + (endX - startX) * 0.5;
        let controlPointY;
        
        if (pathType === 1) {
          // Arc upward
          controlPointY = startY - 80 - Math.random() * 40;
        } else if (pathType === 2) {
          // Arc downward
          controlPointY = startY + 80 + Math.random() * 40;
        } else {
          // Slight variation to direct path
          controlPointY = startY + (endY - startY) * 0.5 + (Math.random() * 40 - 20);
        }
        
        // Set path variables
        leaf.style.setProperty("--start-x", `${startX}px`);
        leaf.style.setProperty("--start-y", `${startY}px`);
        leaf.style.setProperty("--end-x", `${endX}px`);
        leaf.style.setProperty("--end-y", `${endY}px`);
        leaf.style.setProperty("--control-x", `${controlPointX}px`);
        leaf.style.setProperty("--control-y", `${controlPointY}px`);
        
        // Set rotation speed (random direction and speed)
        const rotationSpeed = Math.random() > 0.5 ? 
          `${2 + Math.random() * 3}s` : 
          `${-(2 + Math.random() * 3)}s`;
        leaf.style.setProperty("--rotation-speed", rotationSpeed);
        
        container.appendChild(leaf);
        leaves.push(leaf);
        cleanupElements.push(leaf);
        
        // Handle leaf impact
        leaf.addEventListener("animationend", function onFlight(e) {
          if (e.animationName === "razorLeafFlight") {
            // Create slice effect at impact point
            const slice = document.createElement("div");
            slice.className = "razor-leaf-slice";
            slice.style.left = `${endX}px`;
            slice.style.top = `${endY}px`;
            
            // Random angle for slice
            const sliceAngle = Math.random() * 60 - 30;
            slice.style.setProperty("--slice-angle", `${sliceAngle}deg`);
            
            container.appendChild(slice);
            cleanupElements.push(slice);
            
            // Remove leaf after impact
            leaf.remove();
            leaf.removeEventListener("animationend", onFlight);
            
            // Auto-remove slice after animation
            setTimeout(() => {
              if (slice.parentNode) slice.remove();
            }, 300);
          }
        });
      }, i * delayStep);
    }

    // Final impact effect after all leaves hit
    const totalTime = (leafCount - 1) * delayStep + flightDuration;
    
    const impactTimeout = setTimeout(() => {
      // Create impact burst
      const burst = document.createElement("div");
      burst.className = "razor-leaf-burst";
      burst.style.left = `${endX}px`;
      burst.style.top = `${endY}px`;
      container.appendChild(burst);
      cleanupElements.push(burst);

      // Apply hit effect to opponent
      opponentEl.classList.add("razor-leaf-hit");
      setTimeout(() => opponentEl.classList.remove("razor-leaf-hit"), 300);

      // Create multiple slashes
      for (let i = 0; i < 3; i++) {
        setTimeout(() => {
          const slash = document.createElement("div");
          slash.className = "razor-leaf-slash";
          slash.style.left = `${endX}px`;
          slash.style.top = `${endY}px`;
          
          // Vary slash angle and size
          const slashAngle = -30 + i * 30;
          const slashScale = 0.8 + Math.random() * 0.4;
          slash.style.setProperty("--slash-angle", `${slashAngle}deg`);
          slash.style.setProperty("--slash-scale", slashScale);
          
          container.appendChild(slash);
          cleanupElements.push(slash);
          
          // Auto-remove slash after animation
          setTimeout(() => {
            if (slash.parentNode) slash.remove();
          }, 400);
        }, i * 100);
      }

      // Complete animation
      setTimeout(() => {
        if (burst.parentNode) burst.remove();
        onComplete();
      }, impactDuration);
    }, totalTime);

    // Cleanup function
    return () => {
      clearTimeout(impactTimeout);
      playerEl.classList.remove("razor-leaf-prepare");
      opponentEl.classList.remove("razor-leaf-hit");
      
      cleanupElements.forEach(el => {
        if (el.parentNode) el.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set the move type for proper coloring in the battle UI
RazorLeafAnimation.moveType = "grass";
